(ns web3.auth.okta
  (:require ["@okta/okta-signin-widget" :as okta]
            [web3.errors :as err]
            [web3.env :as env]
            [web3.utils :as utils :include-macros true]
            [web3.persistence.local :as local]
            [web3.re-frame.interceptors :as intr]
            [web3.routing.core :as routing]
            [web3.tracking.ga :as ga]
            [re-frame.core :as rf]
            [day8.re-frame.async-flow-fx]
            [re-frame-simple.db :as db]
            [clojure.spec.alpha :as s]
            [web3.log :as log]
            [web3.routing.utils :as ru]
            [applied-science.js-interop :as j]
            [kitchen-async.promise :as p]
            [web3.tracking.segment :as seg]
            [web3.tracking.mixpanel :as mix]))

(defn js-call-promise [o path & args]
  (p/promise [resolve reject]
    (apply j/call-in o path (conj (vec args)
                                  (fn [err value]
                                    (if err (reject err)
                                            (resolve value)))))))

;; ========== Init ==========

(js/goog.exportSymbol "@okta/okta-signin-widget" okta)

(def okta-widget-element "#osw")

(s/def ::baseUrl string?)
(s/def ::params (s/keys :req-un [::baseUrl]))

(defn init-signin-widget
  []
  (let [okta-params {:baseUrl (:OKTA_BASE_URL env/config)
                     :el okta-widget-element
                     :brandName "HomeServe USA"
                     :features {:selfServiceUnlock true
                                :registration true}
                     :authParams {:clientId (:OKTA_CLIENT_ID env/config)
                                  :pkce  true
                                  :issuer   (:OKTA_OAUTH2_ISSUER env/config)
                                  :redirectUri (:OKTA_REDIRECT_URL env/config)
                                  :scopes ["openid email profile"]
                                  :display "popup"
                                  :grantType "authorization_code"
                                  :responseType ["code"]}}]
    (if (s/valid? ::params okta-params)
      (clj->js okta-params)
      (throw (js/Error. "Okta widget missing required params, this kills the app")))))

(defonce ^js web-auth (atom nil))


;; ========= Okta API ==========

(defn current-time []
  (.getTime (js/Date.)))

(defn handle-auth-error [error]
  (log/println :debug :<auth-callback/error error)
  (rf/dispatch [:events/handle-error
                {:source :okta/error
                 :error error
                 :dispatch [:error/auth.invalid error]}]))

(def analytics-ready
  (p/promisify #(j/call-in js/window [:analytics :ready] %)))

(defn get-mixpanel-id []
  (when (:SEGMENT_ID env/config)
    (p/do (analytics-ready)
          (mix/get-distinct-id))))

(defn login!
  []
  (log/info "In Login")
  (p/let [ga-auth (ga/event! "authorize")
          tracking-id (p/race [(p/timeout 2000)
                               (get-mixpanel-id)])]
    (j/call-in @web-auth [:authClient :token :getWithRedirect]
               (clj->js {:responseType ["code"]
                         :scopes ["openid profile email"]}))
    (log/info "Login: Get With Redirect Called")))

(defn logout!
  []
  (let [remove-item! #(j/call (j/get js/window :localStorage)
                              :removeItem %)]
    (log/info "In logout")
   (-> (j/call-in @web-auth [:authClient :signOut])
       (p/then #(do (log/info "Logout: In catch")
                    (j/call-in @web-auth [:authClient :tokenManager :clear])
                    (mapv remove-item! ["okta-cache-storage"
                                        "okta-token-storage"
                                        "okta-pkce-storage"])
                    (log/info "Logout: Cleared cookies and token manager")
                    (.assign js/location (.. js/window -location -origin))))
       (p/catch* #(do (log/info "Logout: In catch")
                      (j/call-in @web-auth [:authClient :tokenManager :clear])
                      (mapv remove-item! ["okta-cache-storage"
                                          "okta-token-storage"
                                          "okta-pkce-storage"])
                      (log/info "In logout: Cleared cookies and token manager")
                      (.assign js/location (.. js/window -location -origin)))))))

(defn expires-at
  [auth]
  (-> auth
      :expires-in
      (* 1000)
      (+ (current-time))))

(defn expired?
  [auth]
  (not (some-> auth
               :expires-at
               (> (current-time)))))

(defn until-expire
  [auth]
  (some-> auth
          :expires-at
          (* 1000)
          (- (current-time))))

(defn renew-in
  [auth]
  (- (until-expire auth)
     10000))

(defn renew?
  [auth]
  (< (renew-in auth)
     10000))

(defn redirect-path
  []
  (let [path (if (some #(= (ru/get-route)
                           (routing/path-for [%]))
                       [:logout :authorize])
               (routing/path-for [:home])
               (ru/get-route))]
    (log/println :debug "save :okta/redirect" path)
    path))

;; ========== Async Flow ==========

(def auth-flow
  {:id :auth-flow
   :db-path [:async-flows :auth]
   :first-dispatch [:do/auth.init]
   :rules [{:when :seen-any-of?
            :events [:error/auth.invalid]
            :dispatch [:do/auth.sign-in]
            :halt? true}
           {:when :seen-any-of?
            :events [:error/auth.soft-renew-failed]
            :halt? true}
           {:when :seen-all-of?
            :events [:do/auth.authorize
                     :do/auth.set
                     :do/auth.set-user]
            :dispatch [:do/auth.redirect]}
           {:when :seen?
            :events [:do/auth.redirect]
            :dispatch [:success/auth]
            :halt? true}
           {:when :seen-all-of?
            :events [:do/auth.renew
                     :do/auth.set
                     :do/auth.set-user]
            :dispatch [:success/auth]
            :halt? true}]})

(defn init-auth-fx
  []

  (reset! web-auth (okta. (init-signin-widget)))
  ;; ========== FX ==========

  (rf/reg-fx
   :okta/authorize
    (fn []
      (p/do (p/timeout 0)
            (login!))))

  (rf/reg-fx
   :okta/logout
    (fn [_]
      (p/do (p/timeout 0)
            (logout!))))

  (rf/reg-fx
   :auth/get-user
    (fn [token]
      (p/try (p/let [user (j/call-in @web-auth [:authClient :token :getUserInfo] (utils/->js-map token))]
               (log/info :auth/get-user user)
               (rf/dispatch [:do/auth.set-user (utils/->clj-map user)]))
             (p/catch :default e
               (handle-auth-error e)))))
  (rf/reg-fx
   :auth/set-renew-timer
    (fn [auth]
      (log/println :debug :auth/set-renew-timer (renew-in auth))
      (log/time-end :debug "auth-flow")
      (log/group-end :debug)

      (p/do (p/timeout (renew-in auth))
            (log/println :debug :auth/set-renew-timer auth)
            (rf/dispatch [:do/auth.start-flow]))))

  ;; ========== Events ==========

  (intr/reg-event-fx
   :do/auth.logout
    [log/interceptor]
    (fn [cofx _]
      {:local/dissoc [:okta/auth :okta/profile :okta/redirect]
       ::seg/logout nil
       :branch.io/logout nil
       :intercom/shutdown nil
       :okta/logout nil}))

  ;; AFAIK this is just an blank event, used by the re-frame-async auth-flow
  (intr/reg-event-fx :error/auth.invalid (fn [_ _]))

  (intr/reg-event-fx :error/auth.soft-renew-failed
    (fn [{:keys [db]} _]
      {:db (assoc db :okta/loading? false)}))

  (intr/reg-event-fx
   :do/auth.sign-in
    [log/interceptor]
    (fn [cofx _]
      {:local/assoc {:okta/redirect (redirect-path)
                     :document/referrer (.-referrer js/document)}
       :okta/authorize nil}))

  (intr/reg-event-fx
   :do/auth.set-user
    [log/interceptor]
    (fn [db [_ profile]]
      (log/println "in set-user with profile " profile)
      {:local/assoc {:okta/profile profile}}))

  (intr/reg-event-fx
   :do/auth.set
    [log/interceptor]
    (fn [{:keys [db]} [_ auth]]
      (log/info :do/auth.set auth)
      (let [access-object (first (filter #(some? (:access-token %)) auth))
            id-object     (first (filter #(some? (:id-token %)) auth))
            access-token  (:access-token access-object)
            expiry        (:expires-at access-object)
            auth         (zipmap [:id-object :access-object :access-token :expires-at]
                                 [id-object access-object access-token expiry])]
        {:local/assoc {:okta/auth auth}
         :auth/get-user (utils/->js-map access-object)})))

  (intr/reg-event-fx
   :do/auth.redirect
    [(rf/inject-cofx :local/get :okta/redirect)
     log/interceptor]
    (fn [{:keys [okta/redirect]} [_]]
      (log/println :info "load: okta/redirect" redirect)
      {:history/replace redirect}))

  (intr/reg-event-fx
   :do/auth.authorize
    [log/interceptor
     (rf/inject-cofx :local/get :document/referrer)]
    (fn [{:keys [db document/referrer]} _]
      (do
        (log/println :do/auth.authorize-start)
        (p/try
          (p/let [auth-data-js (j/call-in @web-auth [:authClient :token :parseFromUrl])
                  auth-data (utils/->clj-map auth-data-js)]
            (log/println :do/auth.authorize-data auth-data)
            (rf/dispatch [:do/auth.set auth-data]))
          (p/catch :default e
            (handle-auth-error e))))
      {:db (assoc db :document/referrer referrer)}))

  (intr/reg-event-fx
   :do/auth.renew
    [(rf/inject-cofx :local/get :okta/auth) log/interceptor]
    (fn [{:keys [okta/auth db]} [_ {:keys [on-error]
                                    :or {on-error handle-auth-error}}]]
      (let [start (atom (current-time))]
        (log/println :do/auth.renew-start)
        (p/try
          (p/let [access-object-js (j/call-in @web-auth [:authClient :token :renew] (utils/->js-map (:access-object okta/auth)))
                  access-object    (utils/->clj-map access-object-js)]
            (log/println :do/auth.renew access-object)
            (rf/dispatch [:do/auth.set (assoc auth :access-object access-object)]))
          (p/catch :default e
            (handle-auth-error e))))
      nil))

  (intr/reg-event-fx
   :do/auth.init
    [(rf/inject-cofx :local/get :okta/auth)
     (rf/inject-cofx :local/get :okta/profile)
     log/interceptor]
    (fn [{:keys [okta/auth okta/profile db]} [_ {:keys [auth-mode]
                                                   :or {auth-mode :ensure-auth}}]]

      ;; auth-mode can be one of:
      ;;   :renew-softly          only refresh an existing token (silently), never redirect user from page
      ;;   :force-refresh         assume that local token is invalid, forces refresh
      ;;   :ensure-auth (default) ensure that user is authed, refreshing token or redirecting to sign-in/sign-up page

      (log/println :info {:auth-mode auth-mode
                          :auth? (boolean auth)
                          :renew? (renew? auth)
                          :path js/window.location.pathname
                          :auth [(local/local-get :okta/auth)]
                          :root-handler (db/get-in [:db/routers :router/root :handler])})

      {:dispatch (cond
                   (= (db/get-in [:db/routers :router/root :handler]) :authorize) [:do/auth.authorize]
                   (= auth-mode :renew-softly) [:do/auth.renew {:on-error #(rf/dispatch [:error/auth.soft-renew-failed])}]
                   (not auth) [:do/auth.sign-in]
                   (or (= auth-mode :force-refresh)
                       (renew? auth)) [:do/auth.renew]
                   :else [:success/auth])
       :db (if-not (= auth-mode :renew-softly)
             (assoc db :okta/loading? true)
             db)}))

  ;; ========== For coordinating fx ==========

  (rf/reg-fx
   :after
    (fn [fx]
      (rf/dispatch [:do/after fx])))

  (rf/reg-event-fx
   :do/after
    (fn [cofx [_ fx]]
      fx))

  ;; register this event just to avoid warnings
  (intr/reg-event-fx :do/auth.require-auth (fn []))

  (intr/reg-event-fx
   :do/auth.start-flow
    (fn [_ [_ auth-init-options]]
      (log/println :do/auth.start-flow auth-init-options)
      (log/time-start :debug "auth-flow")
      (log/group-start-collapsed :debug "🔑 Auth Flow")
      {:async-flow (-> auth-flow
                       (update :first-dispatch conj auth-init-options))}))

  (intr/reg-event-fx
   :success/auth
    [(rf/inject-cofx :local/get :okta/auth)
     (rf/inject-cofx :local/get :okta/profile)
     log/interceptor]
    (fn [{:keys [db okta/auth okta/profile]} _]
      {:auth/set-renew-timer auth
       :branch.io/set-identity (:email profile)
       :sentry/set-user-context (:email profile)
       :facebook.pixel/init profile
       :hubspot/init profile
       :db (assoc db
             :okta/auth auth
             :okta/profile profile
             :okta/loaded? true
             :okta/loading? false)
       :after {:graphql/retry-errored-queries nil
               :web3.tracking.segment/init nil}}))


  (swap! intr/mixpanel-blacklist into [:do/auth.init :do/auth.require-auth]))